<script lang="ts">
	import classNames from 'classnames';
	import { onMount, untrack } from 'svelte';
	import type { getNewsData } from '../../../utils/getNewsData/getNewsData';
	import { ArtistCardTag } from '../../NewsCard/ArtistCardTag';
	import { GeoCardTag } from '../../NewsCard/GeoCardTag';
	import { Config } from '#lib/constants/config.js';
	import { NewsMixpanelEvents } from '#lib/features/news/constants/mixpanel-events.js';
	import { getHeniNewsArticleOnClickUrl } from '#lib/features/news/utils/getHeniNewsArticleOnClickUrl/getHeniNewsArticleOnClickUrl.js';
	import { NewsRoutes } from '#lib/websites/news/constants/routes.js';
	import { ChevronLeftIcon } from '$global/assets/icons/ChevronLeftIcon';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { ShareIcon } from '$global/assets/icons/ShareIcon';
	import { Button } from '$global/components/Button';
	import { createDialogStores } from '$global/components/Dialog';
	import { LinkButton } from '$global/components/LinkButton';
	import { ShareDialog } from '$global/components/Share/ShareDialog';
	import {
		TableHeaderRow,
		TableHeader,
		TableBody,
		TableRow,
		TableCell,
	} from '$global/components/Table';
	import { Txt } from '$global/components/Txt';
	import { abbreviateNumber } from '$global/utils/abbreviateNumber/abbreviateNumber';
	import { track } from '$global/utils/track';

	export type NewsChart = Awaited<
		ReturnType<typeof getNewsData>
	>['charts'][number] & {
		id: string;
		initialOffset?: number;
	};

	interface Props {
		chart: Partial<NewsChart>;
		id?: string | undefined;
		showGlobalNav?: boolean;
		classes?: {
			top?: string;
		};
	}

	let { chart, id, classes, showGlobalNav }: Props = $props();
	let showViewButtonColumn = $derived(
		chart.expect_view_link ||
			(chart.entries || []).some(
				(entry: NewsChart['entries'][number]) => entry?.news_link?.[0]?.item?.id
			)
	);

	let offset = $state(untrack(() => chart.initialOffset || 0));
	let limit = $derived(chart?.entries?.[0]?.title ? 5 : 6);

	// `chart` is a Partial, so the header object is optional at the type level
	// even though every config carries one.
	let columnNames = $derived(chart.column_names?.[0] ?? {});

	let headers = $derived([
		...Object.values(columnNames),
		...(showViewButtonColumn ? [''] : []),
	]);

	let tableIntro = $derived(`HENI News: ${chart.title}`);
	let tableShareUrl = $derived(`${Config.Domain}${NewsRoutes.Data}#${id}`);

	let handleClickPlatform = $derived((platform: string) => {
		track(NewsMixpanelEvents.ShareArticle, { id: chart?.id, platform });
	});

	const dataCyPrefix = 'news-data-chart';
	const dialogStores = createDialogStores();

	const handleOpenShareDialog = () => {
		if (showGlobalNav) {
			dialogStores.states.open.set(true);
		} else {
			(window.location as unknown as string) = `/news?shareApp=1&text=${tableIntro}&url=${tableShareUrl}`;
		}
	};

	const handleClickNext = () => {
		offset = offset + limit;
	};

	const handleClickPrev = () => {
		offset = offset - limit;
	};

	let ref1: HTMLSpanElement | null = $state(null);
	let ref2: HTMLSpanElement | null = $state(null);

	let columnWidth1: number | null = $state(null);
	let columnWidth2: number | null = $state(null);

	onMount(() => {
		const listener = () => {
			if (ref1) {
				columnWidth1 = ref1?.getBoundingClientRect?.()?.width;
			}

			if (ref2) {
				columnWidth2 = ref2?.getBoundingClientRect?.()?.width;
			}
		};

		window.addEventListener('resize', listener);
		return () => window.removeEventListener('resize', listener);
	});

	$effect(() => {
		if (ref1) {
			columnWidth1 = ref1?.getBoundingClientRect?.()?.width;
		}

		if (ref2) {
			columnWidth2 = ref2?.getBoundingClientRect?.()?.width;
		}
	});
</script>

<div class="flex h-full flex-col">
	<div class="flex h-[calc(100%-32px)] flex-col">
		<div class={classNames('flex flex-col gap-0.5 sm:h-[62px]', classes?.top)}>
			<Txt variant="h5" class="leading-[1.25rem]">
				{chart.title}
			</Txt>
			{#if chart.interval}
				<Txt variant="body2" class="font-[500]" component="span">
					{' '}({chart.interval})
				</Txt>
			{/if}
		</div>

		{#if chart.intro}
			<Txt variant="body2" class="mt-2">
				{chart.intro}
			</Txt>
		{/if}

		<div class="flex-1">
			<table
				class={classNames('w-full table-fixed bg-white', {
					invisible: columnWidth1 === null,
				})}
			>
				<TableHeaderRow dataCy={dataCyPrefix} class="h-[49px] border-x-0">
					{#each headers as header, i}
						<TableHeader
							dataCy={dataCyPrefix}
							width={(() => {
								if (i === 1 || (i === 2 && chart.column_names?.[2])) {
									const offset =
										columnNames.column_3 && chart.type === 'price' ? 24 : 16;
									return columnWidth1 && columnWidth1 + offset < 90
										? `${columnWidth1 + offset}px`
										: '90px';
								}

								if (i === 2 && columnNames.column_3) {
									if (columnWidth2 && columnWidth2 + 16 < 98) {
										if (columnWidth2 + 16 < 50) {
											return '50px';
										} else {
											return `${columnWidth2 + 16}px`;
										}
									}

									return '98px';
								}

								if (i === 2 && showViewButtonColumn) {
									return '63px';
								}
							})()}
							class={classNames('border-t-0 py-2 [&>p]:inline-block', {
								'pl-[34px]': !i,
								'px-0': i === 1,
								'pr-0': i === 2 && Object.values(columnNames).length === 3,
							})}
						>
							{#snippet custom()}
								<Txt variant="label3" component="div">
									{#if i === 1}
										<span bind:this={ref1}>
											{header}
										</span>
									{:else if i === 2 && columnNames.column_3}
										<span bind:this={ref2}>
											{header}
										</span>
									{:else}
										{header}
									{/if}
								</Txt>
							{/snippet}
						</TableHeader>
					{/each}
				</TableHeaderRow>

				<TableBody
					dataCy={dataCyPrefix}
					class="h-[calc(100%-49px)] overflow-y-scroll border-b"
				>
					{#if chart.entries}
						{#each chart.entries.slice(offset, limit + offset) as entry, i}
							{@const article = entry.news_link?.[0]?.item}
							<TableRow index={1} dataCy={dataCyPrefix} class="border-x-0">
								<TableCell
									dataCy={dataCyPrefix}
									class={classNames({
										'border-b-0': i === limit - 1,
									})}
								>
									{#snippet custom()}
										<div class="flex items-center gap-4">
											<Txt variant="body2" class="font-[500]">
												{offset + i + 1}
											</Txt>
											{#if entry.tag?.[0]?.item?.__typename === 'HENI_News_Artists'}
												<ArtistCardTag
													dataCy={dataCyPrefix}
													name={entry.tag?.[0]?.item?.name}
													showNewVersion
													class="max-w-[calc(100%-30px)] [&>p]:overflow-hidden [&>p]:text-ellipsis [&>p]:whitespace-nowrap"
												/>
											{:else if entry.tag?.[0]?.item?.__typename === 'HENI_News_Geotag'}
												<GeoCardTag
													dataCy={dataCyPrefix}
													name={entry.tag?.[0]?.item?.name}
													showNewVersion
													class="max-w-[calc(100%-30px)] [&>p]:overflow-hidden [&>p]:text-ellipsis [&>p]:whitespace-nowrap"
												/>
											{:else}
												<div class="max-w-[calc(100%-30px)]">
													<Txt
														variant="body3"
														class="mb-0.5 overflow-hidden font-[500] text-ellipsis whitespace-nowrap"
													>
														{entry.title}
													</Txt>
													{#if entry.subtitle}
														<Txt
															variant="body3"
															class="overflow-hidden text-ellipsis whitespace-nowrap"
														>
															{entry.subtitle}
														</Txt>
													{/if}
												</div>
											{/if}
										</div>
									{/snippet}
								</TableCell>
								<TableCell
									dataCy={dataCyPrefix}
									class={classNames('px-0', {
										'border-b-0': i === limit - 1,
									})}
								>
									{#snippet custom()}
										{#if columnNames.column_2.trim()}
											<Txt variant="body3">
												{#if chart.type === 'price'}
													{`$${abbreviateNumber(entry?.value_1)}`.toUpperCase()}
												{:else}
													{entry?.value_1}
												{/if}
											</Txt>
										{/if}
									{/snippet}
								</TableCell>
								{#if entry?.value_2}
									<TableCell
										dataCy={dataCyPrefix}
										class={classNames('pr-0', {
											'border-b-0': i === limit - 1,
										})}
									>
										{#snippet custom()}
											<Txt variant="body3">
												{#if chart.type === 'price'}
													{`$${abbreviateNumber(entry?.value_2 as number)}`.toUpperCase()}
												{:else}
													{entry?.value_2}
												{/if}
											</Txt>
										{/snippet}
									</TableCell>
								{/if}
								{#if showViewButtonColumn}
									<TableCell
										dataCy={dataCyPrefix}
										class={classNames('pr-0', {
											'border-b-0': i === limit - 1,
										})}
									>
										{#snippet custom()}
											{#if article}
												<div class="flex justify-end">
													<LinkButton
														size="xs"
														dataCy={`${dataCyPrefix}-view`}
														variant="secondary"
														newTab
														onclick={() => {
															if (!showGlobalNav) {
																// eslint-disable-next-line @typescript-eslint/no-explicit-any
																(window as any).location =
																	article?.__typename === 'News_HENI_Article'
																		? `${NewsRoutes.MobileArticle}/${article.slug}?global_nav=0`
																		: getHeniNewsArticleOnClickUrl(
																				article?.url
																			);
															}
														}}
														href={showGlobalNav
															? article?.__typename === 'News_HENI_Article'
																? `${NewsRoutes.Article}/${article.slug}`
																: `${NewsRoutes.View}/${article?.id}`
															: null}
													>
														View
													</LinkButton>
												</div>
											{/if}
										{/snippet}
									</TableCell>
								{/if}
							</TableRow>
						{/each}
					{/if}
				</TableBody>
			</table>
		</div>
	</div>

	<div class="mt-4 flex h-[32px] items-center justify-between">
		<div class="flex items-center gap-4">
			{#if chart.entries && chart.entries.length > limit}
				<div class="flex items-center gap-2">
					<Button
						dataCy={`${dataCyPrefix}-next`}
						aria-label="Previous entries"
						class="h-[2rem] max-w-[2rem]"
						variant="secondary"
						size="xs"
						disabled={!offset}
						onclick={handleClickPrev}
					>
						<ChevronLeftIcon class="h-2 w-2" />
					</Button>

					<Button
						dataCy={`${dataCyPrefix}-prev`}
						aria-label="Next entries"
						class="h-[2rem] max-w-[2rem]"
						variant="secondary"
						size="xs"
						onclick={handleClickNext}
						disabled={!chart.entries?.[offset + limit]}
					>
						<ChevronRightIcon class="h-2 w-2" />
					</Button>
				</div>
			{/if}

			<Txt variant="body3" class=" text-gray-500">
				{#if chart.source}
					{chart.source}
				{/if}
			</Txt>
		</div>

		<button
			class="border-none"
			onclick={handleOpenShareDialog}
			aria-label="Share chart"
			data-cy={`${dataCyPrefix}-share-button`}
		>
			<ShareIcon class="h-5 w-5" />
		</button>
	</div>
</div>

<ShareDialog
	colored
	{dialogStores}
	link={`${Config.Domain}${NewsRoutes.Data}#${id}`}
	intro={tableIntro}
	dataCy={dataCyPrefix}
	showTwitter={false}
	onClickPlatform={handleClickPlatform}
/>
